import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { TranslateFunction } from 'react-localize-redux';

import { ChevronRight as ChevronRightIcon } from '../../icons/ChevronRight';
import { Typography } from '..';

export interface PriceTagProps {
  className?: string;
  hasMenuOptions?: boolean;
  horizontalAlign?: 'left' | 'right';
  label?: string;
  localisedPrice: string;
  translate: TranslateFunction;
  variant?: 'large' | 'medium' | 'small';
  verticalAlign?: 'top' | 'center' | 'bottom';
}

const useStyles = makeStyles((theme: Theme) => ({
  priceTag: {
    alignItems: 'flex-start',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '36px',

    '& .fd-text__h3': {
      fontSize: theme.typography.pxToRem(16),
    },
    '&.positionRight': {
      alignItems: 'flex-end',
      minHeight: 'unset',

      '& .itemHasOptions': {
        display: 'none',
      },
    },
  },
  price: {
    width: '100%',
  },
  label: {
    color: theme.palette.grey[800],
    display: 'inline-block',
    fontSize: theme.typography.pxToRem(10),
    lineHeight: 1.2,
    margin: 0,
    padding: 0,
    textTransform: 'uppercase',
  },
  priceBox: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    width: '100%',
  },
  alignTop: {
    justifyContent: 'flex-start',
  },
  alignBottom: {
    justifyContent: 'flex-end',
  },
  alignRight: {
    '& .fd-text__h3': {
      textAlign: 'right',
    },
  },
  variantLarge: {
    '& .fd-text__h3': {
      fontSize: theme.typography.pxToRem(21),
    },
  },
  variantSmall: {
    minHeight: '24px',

    '& .fd-text__h3': {
      fontSize: theme.typography.pxToRem(13),
    },
  },
}));

export function PriceTag(props: PriceTagProps): JSX.Element {
  const {
    className,
    hasMenuOptions = false,
    horizontalAlign = 'left',
    label = 'From',
    localisedPrice,
    translate,
    variant = 'medium',
    verticalAlign = 'center',
    ...rest
  } = props;
  const classes = useStyles();

  const priceTagStyles = clsx(
    classes.priceTag,
    'fd-price',
    className || '',
    variant === 'large' && classes.variantLarge,
    variant === 'small' && classes.variantSmall,
    verticalAlign === 'top' && classes.alignTop,
    verticalAlign === 'bottom' && classes.alignBottom,
    horizontalAlign === 'right' && classes.alignRight,
  );

  return (
    <div className={priceTagStyles} {...rest}>
      {hasMenuOptions && (
        <Typography className={classes.label} variant="body2">
          {`${translate(label)}`}
        </Typography>
      )}

      <div className={classes.priceBox}>
        <Typography className={classes.price} variant="h3">
          {localisedPrice}
        </Typography>

        {hasMenuOptions && (
          <ChevronRightIcon className="itemHasOptions" size="xs" />
        )}
      </div>
    </div>
  );
}
